import { useEffect, useRef, useState } from "react";

import type {
  ActiveControlSchemeV1,
  ControlInputCapabilitiesV1,
  ControlSchemePreferenceV1,
  LastControlInputV1,
} from "@gordonland/ui";
import { PLACEABLE_BLOCK_IDS_V1, type PlaceableBlockId } from "@gordonland/world-format";

import {
  CONTROL_SCHEME_COPY_ZH_CN_V1,
  createControlSchemePreferenceRepositoryV1,
  discoverControlInputCapabilitiesV1,
  planControlSchemeSwitchV1,
} from "./control-scheme.js";
import {
  mergeDevelopmentDiagnosticsProbeV1,
  type DevelopmentDiagnosticsSnapshotV1,
} from "./development-diagnostics.js";
import { createGameInputControllerV1, type GameInputControllerV1, type GameInputStateV1 } from "./game-input.js";
import type { LocalSandboxPersistenceSnapshotV1 } from "./local-sandbox-storage.js";
import type { BuildInteractionSnapshotV1, P0020PlayableRuntimeV1 } from "./p0-020-playable.js";
import {
  BuildTray,
  ControlSchemeDialog,
  DiagnosticsHud,
  GameMenu,
  Icon,
  InformationDialog,
  Modal,
} from "./shell-components.js";
import { BLOCK_LABELS } from "./shell-copy.js";

type QaModeV1 = "p0-020" | "p0-021" | "p0-022" | "p0-023" | "p0-024" | null;
type OverlayV1 = "blocks" | "control" | "menu" | string | null;

function qaMode(): QaModeV1 {
  if (typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get("qa");
  return value === "p0-020" || value === "p0-021" || value === "p0-022" || value === "p0-023" || value === "p0-024" ? value : null;
}

function feedbackText(snapshot: BuildInteractionSnapshotV1 | null): string {
  if (snapshot === null || snapshot.feedback.state === "explore") return "探索";
  if (snapshot.feedback.state === "pending") return "正在确认更改…";
  if (snapshot.feedback.state === "confirmed") return "更改已保存。";
  if (snapshot.feedback.state === "rejected") return "更改未保存。";
  if (snapshot.feedback.state === "place-invalid") {
    return snapshot.feedback.reason === "out-of-reach" ? "请靠近一些再建造。" : "请选择空位。";
  }
  if (snapshot.feedback.state === "remove-invalid") return "请选择要移除的方块。";
  return BLOCK_LABELS[snapshot.selectedBlockId - 1] ?? "草甸草方块";
}

function selectedBlock(runtime: P0020PlayableRuntimeV1 | null, delta: -1 | 1): void {
  if (runtime === null) return;
  const current = runtime.snapshot().build.selectedBlockId;
  const index = PLACEABLE_BLOCK_IDS_V1.indexOf(current);
  const next = PLACEABLE_BLOCK_IDS_V1[(index + delta + PLACEABLE_BLOCK_IDS_V1.length) % PLACEABLE_BLOCK_IDS_V1.length];
  if (next !== undefined) runtime.selectBlock(next);
}

export function App() {
  const qa = qaMode();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const runtimeRef = useRef<P0020PlayableRuntimeV1 | null>(null);
  const inputRef = useRef<GameInputControllerV1 | null>(null);
  const repositoryRef = useRef(createControlSchemePreferenceRepositoryV1());
  const [capabilities] = useState<ControlInputCapabilitiesV1>(discoverControlInputCapabilitiesV1);
  const [status, setStatus] = useState("正在准备 GordonLand…");
  const [failed, setFailed] = useState(false);
  const [build, setBuild] = useState<BuildInteractionSnapshotV1 | null>(null);
  const [persistence, setPersistence] = useState<LocalSandboxPersistenceSnapshotV1 | null>(null);
  const [diagnostics, setDiagnostics] = useState<DevelopmentDiagnosticsSnapshotV1 | null>(null);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(qa === "p0-024");
  const diagnosticsRef = useRef<DevelopmentDiagnosticsSnapshotV1 | null>(null);
  const diagnosticsLiveRef = useRef<DevelopmentDiagnosticsSnapshotV1 | null>(null);
  const diagnosticsOverrideRef = useRef<DevelopmentDiagnosticsSnapshotV1 | null>(null);
  const [preferenceReady, setPreferenceReady] = useState(false);
  const [firstEntryComplete, setFirstEntryComplete] = useState(false);
  const [preference, setPreference] = useState<ControlSchemePreferenceV1>("auto");
  const [activeScheme, setActiveScheme] = useState<ActiveControlSchemeV1>(
    capabilities.keyboard ? "keyboard_mouse" : "touch",
  );
  const [lastInput, setLastInput] = useState<LastControlInputV1>(null);
  const [announcement, setAnnouncement] = useState("");
  const [pointerLockFailure, setPointerLockFailure] = useState(false);
  const [overlay, setOverlay] = useState<OverlayV1>(null);
  const [sprintLatched, setSprintLatched] = useState(false);
  const stateRef = useRef<GameInputStateV1>({ activeScheme, blocked: true, preference });
  const qaStateRef = useRef({ firstEntryComplete, lastInput });
  const commitSwitchRef = useRef<(
    nextPreference: ControlSchemePreferenceV1,
    evidence?: ActiveControlSchemeV1,
    alreadyReset?: boolean,
  ) => Promise<boolean>>(async () => false);

  const blocked = !preferenceReady || !firstEntryComplete || overlay !== null;

  useEffect(() => {
    stateRef.current = { activeScheme, blocked, preference };
    qaStateRef.current = { firstEntryComplete, lastInput };
  }, [activeScheme, blocked, firstEntryComplete, lastInput, preference]);

  useEffect(() => {
    commitSwitchRef.current = async (nextPreference, evidence, alreadyReset = false): Promise<boolean> => {
      const plan = planControlSchemeSwitchV1(capabilities, nextPreference, evidence);
      if (plan === null) {
        setAnnouncement(CONTROL_SCHEME_COPY_ZH_CN_V1.unsupported);
        return false;
      }
      if (!alreadyReset) inputRef.current?.reset("control-scheme-switch");
      await repositoryRef.current.save(plan.preference);
      setPreference(plan.preference);
      setActiveScheme(plan.activeScheme);
      setFirstEntryComplete(true);
      setOverlay(null);
      setAnnouncement(plan.announcement);
      return true;
    };
  }, [capabilities]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (qa === "p0-020" || qa === "p0-021" || qa === "p0-023" || qa === "p0-024") {
        if (!cancelled) {
          setPreference("auto");
          setActiveScheme("keyboard_mouse");
          setFirstEntryComplete(true);
          setPreferenceReady(true);
        }
        return;
      }
      const loaded = await repositoryRef.current.load();
      if (cancelled) return;
      if (loaded.kind === "selected") {
        const plan = planControlSchemeSwitchV1(capabilities, loaded.preference);
        if (plan !== null) {
          setPreference(plan.preference);
          setActiveScheme(plan.activeScheme);
          setFirstEntryComplete(true);
        }
      }
      setPreferenceReady(true);
    })();
    return () => { cancelled = true; };
  }, [capabilities, qa]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    let cancelled = false;
    let runtime: P0020PlayableRuntimeV1 | null = null;
    let unsubscribeBuild: (() => void) | null = null;
    let unsubscribeDiagnostics: (() => void) | null = null;
    const forceBackend = new URLSearchParams(window.location.search).get("backend") === "webgl2" ? "webgl2" : undefined;
    const requestedStorage = new URLSearchParams(window.location.search).get("storage");
    const persistenceMode = qa === "p0-023"
      ? requestedStorage === "indexeddb" || requestedStorage === "opfs" ? requestedStorage : "auto"
      : qa === null ? "auto" : "ephemeral";

    void (async () => {
      const { createP0020PlayableRuntimeV1 } = await import("./p0-020-playable.js");
      runtime = await createP0020PlayableRuntimeV1(canvas, (nextStatus) => {
        if (!cancelled) setStatus(nextStatus);
      }, forceBackend, persistenceMode);
      if (cancelled) {
        runtime.dispose();
        return;
      }
      runtimeRef.current = runtime;
      window.__GORDONLAND_P0_020__ = runtime;
      window.__GORDONLAND_P0_021__ = runtime;
      window.__GORDONLAND_P0_023__ = runtime;
      const subscription = runtime.subscribeBuild((snapshot) => {
        if (!cancelled) {
          setBuild(snapshot);
          setPersistence(runtime?.snapshot().persistence ?? null);
        }
      });
      unsubscribeBuild = () => subscription.unsubscribe();
      const diagnosticsSubscription = runtime.subscribeDiagnostics((snapshot) => {
        diagnosticsLiveRef.current = snapshot;
        if (diagnosticsOverrideRef.current === null && !cancelled) {
          diagnosticsRef.current = snapshot;
          setDiagnostics(snapshot);
        }
      });
      unsubscribeDiagnostics = () => diagnosticsSubscription.unsubscribe();
      if (qa === "p0-024") {
        window.__GORDONLAND_P0_024__ = Object.freeze({
          clearProbe(): void {
            diagnosticsOverrideRef.current = null;
            const live = diagnosticsLiveRef.current;
            if (live !== null) {
              diagnosticsRef.current = live;
              setDiagnostics(live);
            }
          },
          injectProbe(overrides: Readonly<Record<string, unknown>>): void {
            const base = diagnosticsOverrideRef.current ?? diagnosticsLiveRef.current;
            if (base === null) throw new TypeError("P0-024 diagnostics are unavailable");
            const next = mergeDevelopmentDiagnosticsProbeV1(base, overrides);
            diagnosticsOverrideRef.current = next;
            diagnosticsRef.current = next;
            setDiagnostics(next);
          },
          snapshot(): DevelopmentDiagnosticsSnapshotV1 {
            const current = diagnosticsRef.current;
            if (current === null) throw new TypeError("P0-024 diagnostics are unavailable");
            return current;
          },
        });
      }
      const input = createGameInputControllerV1({
        canvas,
        runtime,
        getState: () => stateRef.current,
        onAutomaticInput: (scheme) => {
          void commitSwitchRef.current("auto", scheme, true);
        },
        onInputKind: setLastInput,
        onMenu: () => setOverlay("menu"),
        onPointerLockFailure: () => setPointerLockFailure(true),
      });
      inputRef.current = input;
      window.__GORDONLAND_P0_022__ = Object.freeze({
        async clearPreference(): Promise<void> {
          await repositoryRef.current.clear();
        },
        snapshot() {
          return Object.freeze({
            activeScheme: stateRef.current.activeScheme,
            build: runtime?.snapshot().build ?? null,
            capabilities,
            firstEntrySelectionComplete: qaStateRef.current.firstEntryComplete,
            input: input.snapshot(),
            lastInput: qaStateRef.current.lastInput,
            preference: stateRef.current.preference,
            runtimeInput: runtime?.snapshotInput() ?? null,
          });
        },
        async writeRawPreference(value: unknown): Promise<void> {
          await repositoryRef.current.writeRawForQa(value);
        },
      });
    })().catch((error: unknown) => {
      if (cancelled) return;
      setFailed(true);
      setStatus(error instanceof Error ? `三维画面启动失败：${error.message}` : "三维画面启动失败");
    });

    return () => {
      cancelled = true;
      delete window.__GORDONLAND_P0_020__;
      delete window.__GORDONLAND_P0_021__;
      delete window.__GORDONLAND_P0_022__;
      delete window.__GORDONLAND_P0_023__;
      delete window.__GORDONLAND_P0_024__;
      unsubscribeBuild?.();
      unsubscribeDiagnostics?.();
      diagnosticsLiveRef.current = null;
      diagnosticsOverrideRef.current = null;
      diagnosticsRef.current = null;
      inputRef.current?.dispose();
      inputRef.current = null;
      runtimeRef.current = null;
      runtime?.dispose();
    };
  }, [capabilities, qa]);

  useEffect(() => {
    if (blocked) inputRef.current?.reset("overlay-or-first-entry");
  }, [blocked]);

  const selectedBlockId: PlaceableBlockId = build?.selectedBlockId ?? 1;
  const openMenu = (): void => {
    inputRef.current?.reset("menu-open");
    setOverlay("menu");
  };
  const closeOverlay = (): void => setOverlay(null);
  const touchHandlers = (region: "look" | "move") => region === "look" ? inputRef.current?.lookRegionHandlers : inputRef.current?.moveRegionHandlers;
  const setSprint = (): void => {
    const next = !sprintLatched;
    setSprintLatched(next);
    inputRef.current?.setSprintLatched(next);
  };

  return (
    <main
      className={`game-shell game-shell--${activeScheme}`}
      data-p0-020-state={failed ? "failed" : status === "三维岛屿已就绪" ? "ready" : "loading"}
      data-p0-021-state={failed ? "failed" : build === null ? "loading" : "ready"}
      data-p0-022-state={failed ? "failed" : !preferenceReady || build === null ? "loading" : "ready"}
      data-p0-023-state={failed ? "failed" : build === null ? "loading" : "ready"}
      data-p0-024-state={failed ? "failed" : diagnostics === null ? "loading" : "ready"}
      data-active-scheme={activeScheme}
      data-control-preference={preference}
      data-build-feedback={qa === "p0-021" || qa === "p0-022" || qa === "p0-023" ? build?.feedback.state : undefined}
      data-build-revision={qa === "p0-021" || qa === "p0-022" || qa === "p0-023" ? build?.canonicalRevision : undefined}
      data-save-generation={qa === "p0-023" ? persistence?.saveGenerationDecimal : undefined}
      data-storage-backend={qa === "p0-023" ? persistence?.backend : undefined}
      data-build-remeshes={qa === "p0-021" ? build?.totalRemeshes : undefined}
      data-build-last-remesh={qa === "p0-021" ? build?.lastRemeshedSubchunks.join(",") : undefined}
      data-build-owner-revisions={qa === "p0-021" ? build?.installedOwnerRevisions.join(",") : undefined}
    >
      <canvas ref={canvasRef} className="game-canvas" aria-label="游戏画面" />

      {capabilities.touch && firstEntryComplete ? (
        <div
          className={`touch-look-region${activeScheme === "touch" ? " touch-look-region--primary" : " touch-look-region--secondary"}`}
          aria-label="触控视角区域"
          onPointerDown={(event) => touchHandlers("look")?.pointerDown(event.nativeEvent, event.currentTarget)}
          onPointerMove={(event) => touchHandlers("look")?.pointerMove(event.nativeEvent)}
          onPointerUp={(event) => touchHandlers("look")?.pointerUp(event.nativeEvent)}
          onPointerCancel={(event) => touchHandlers("look")?.pointerUp(event.nativeEvent)}
        />
      ) : null}

      <header className="world-card" aria-label="当前世界">
        <span className="brand-sigil" aria-hidden="true">G</span>
        <span>
          <strong>本地沙盒</strong>
          <small>已保存在此设备</small>
          <em>昼夜与天气暂不可用。</em>
        </span>
      </header>

      {qa === "p0-024" && diagnostics !== null ? (
        diagnosticsOpen
          ? <DiagnosticsHud snapshot={diagnostics} onClose={() => setDiagnosticsOpen(false)} />
          : <button className="diagnostics-toggle" type="button" aria-expanded="false" onClick={() => setDiagnosticsOpen(true)}>显示诊断</button>
      ) : null}

      <div className="top-actions">
        <div className="presence-chip" aria-label="1 位探索者"><Icon name="presence" /><span>1 位探索者</span></div>
        <button className="menu-button" type="button" onClick={openMenu}><Icon name="menu" /><span>菜单</span></button>
      </div>

      <div className="scene-status" role="status" aria-live="polite">
        <span className={failed ? "status-dot status-dot--failed" : "status-dot"} />
        {status}
      </div>

      {activeScheme === "keyboard_mouse" && firstEntryComplete ? (
        <div className="desktop-controls">
          <button className="pointer-lock-button" type="button" onClick={() => { void inputRef.current?.requestPointerLock(); }}>继续鼠标控制</button>
          <p className="control-hint">WASD 移动 · 鼠标视角 · 滚轮缩放 · 空格跳跃 · B 建造</p>
        </div>
      ) : null}

      {firstEntryComplete ? (
        <div className="mode-control" role="group" aria-label="探索 / 建造">
          <button type="button" aria-label="探索" aria-pressed={build?.interactionMode !== "build"} onClick={() => runtimeRef.current?.setInteractionMode("explore")}><Icon name="explore" /><span>探索</span></button>
          <button type="button" aria-label="建造" aria-pressed={build?.interactionMode === "build"} onClick={() => runtimeRef.current?.setInteractionMode("build")}><Icon name="build" /><span>建造</span></button>
        </div>
      ) : null}

      {activeScheme === "keyboard_mouse" && firstEntryComplete && build?.interactionMode === "build" ? (
        <section className="desktop-build-tray" aria-label="建造方块">
          <div className="tray-heading"><strong>{BLOCK_LABELS[selectedBlockId - 1]}</strong><small>Shift + 滚轮切换</small></div>
          <BuildTray selectedBlockId={selectedBlockId} onSelect={(blockId) => runtimeRef.current?.selectBlock(blockId)} />
          <div className="desktop-build-actions" aria-label="建造操作">
            <button type="button" onClick={() => { void runtimeRef.current?.requestPlace(); }}><Icon name="place" /><span>放置</span></button>
            <button type="button" onClick={() => { void runtimeRef.current?.requestRemove(); }}><Icon name="remove" /><span>移除</span></button>
          </div>
        </section>
      ) : null}

      {activeScheme === "touch" && firstEntryComplete ? (
        <div className="touch-controller" aria-label="触屏操作">
          <div
            className="move-pad"
            aria-label="移动"
            onPointerDown={(event) => touchHandlers("move")?.pointerDown(event.nativeEvent, event.currentTarget)}
            onPointerMove={(event) => touchHandlers("move")?.pointerMove(event.nativeEvent)}
            onPointerUp={(event) => touchHandlers("move")?.pointerUp(event.nativeEvent)}
            onPointerCancel={(event) => touchHandlers("move")?.pointerUp(event.nativeEvent)}
          ><span>移动</span><i aria-hidden="true" /></div>
          <div className="touch-actions touch-actions--movement">
            <button type="button" onClick={() => runtimeRef.current?.requestJump()}><Icon name="jump" /><span>跳跃</span></button>
            <button type="button" aria-pressed={sprintLatched} onClick={setSprint}><Icon name="sprint" /><span>{sprintLatched ? "疾跑 · 已开启" : "疾跑"}</span></button>
          </div>
          <div className="touch-actions touch-actions--build">
            <button type="button" onClick={() => { void runtimeRef.current?.requestPlace(); }}><Icon name="place" /><span>放置</span></button>
            <button type="button" onClick={() => { void runtimeRef.current?.requestRemove(); }}><Icon name="remove" /><span>移除</span></button>
          </div>
          {build?.interactionMode === "build" ? (
            <div className="mobile-block-selector" aria-label="方块选择">
              <button type="button" aria-label="上一个方块" onClick={() => selectedBlock(runtimeRef.current, -1)}><Icon name="previous" /></button>
              <button className="selected-block-button" type="button" aria-label="方块列表" onClick={() => { inputRef.current?.reset("block-sheet-open"); setOverlay("blocks"); }}>
                <img src={`/assets/p0-022/icons/block-${String(selectedBlockId).padStart(2, "0")}-v1.svg`} alt="" aria-hidden="true" />
                <span>{BLOCK_LABELS[selectedBlockId - 1]}</span>
              </button>
              <button type="button" aria-label="下一个方块" onClick={() => selectedBlock(runtimeRef.current, 1)}><Icon name="next" /></button>
            </div>
          ) : null}
        </div>
      ) : null}

      {build?.interactionMode === "build" ? <div className="build-feedback" role="status" aria-live="polite">{feedbackText(build)}</div> : null}
      {announcement !== "" ? <div className="sr-announcement" role="status" aria-live="polite">{announcement}</div> : null}
      {pointerLockFailure ? <div className="pointer-lock-failure" role="alert">鼠标视角控制不可用。</div> : null}

      {preferenceReady && !firstEntryComplete ? (
        <ControlSchemeDialog capabilities={capabilities} firstEntry={true} selectedPreference={preference} onSelect={(nextPreference) => { void commitSwitchRef.current(nextPreference); }} />
      ) : null}
      {overlay === "control" ? (
        <ControlSchemeDialog capabilities={capabilities} firstEntry={false} selectedPreference={preference} onClose={() => setOverlay("menu")} onSelect={(nextPreference) => { void commitSwitchRef.current(nextPreference); }} />
      ) : null}
      {overlay === "menu" ? <GameMenu onClose={closeOverlay} onOpenControlScheme={() => setOverlay("control")} onOpenPanel={setOverlay} /> : null}
      {overlay === "blocks" ? (
        <Modal labelledBy="block-sheet-heading" className="block-sheet" onClose={closeOverlay}>
          <button className="modal-close" type="button" aria-label="关闭方块列表" onClick={closeOverlay}><Icon name="close" /></button>
          <h1 id="block-sheet-heading">方块列表</h1>
          <BuildTray compact={true} selectedBlockId={selectedBlockId} onSelect={(blockId) => { runtimeRef.current?.selectBlock(blockId); closeOverlay(); }} />
        </Modal>
      ) : null}
      {overlay !== null && !["blocks", "control", "menu"].includes(overlay) ? <InformationDialog heading={overlay} onClose={() => setOverlay("menu")} /> : null}

      {qa === "p0-020" ? (
        <aside className="qa-controls" aria-label="P0-020 QA controls">
          <button type="button" onClick={() => runtimeRef.current?.orbitBy(Math.PI / 8)}>Orbit</button>
          <button type="button" onClick={() => runtimeRef.current?.moveForward(18, false)}>Walk</button>
          <button type="button" onClick={() => runtimeRef.current?.setRequestedArm(2.2)}>Zoom near</button>
          <button type="button" onClick={() => { void runtimeRef.current?.setObstacle(false); }}>Clear obstruction</button>
        </aside>
      ) : null}
    </main>
  );
}
